import { faPlus, faSyringe, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Col, Input, message, Modal, Row, Card, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../../../../lib/utils'
import DesktopMedicalHistoryNav from './Nav'
import DatePicker from '../../../Reusable Components/DatePicker'
import { updateSurgeries } from '../../api/api'
import patMedHistReducer from '../../utils/reducer'
import '../style/DesktopSurgeries.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const { Text } = Typography

const DesktopSurgeries = ({ setFacet, setSaving }) => {
  const patMedHist = useSelector((state) => state.patMedHist.data) || {}
  const [surgeryList, setSurgeryList] = useState(patMedHist.surgicalHistory?.list || [])
  const [inputValue, setInputValue] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedYear, setSelectedYear] = useState(null)
  const [showYearPicker, setShowYearPicker] = useState(false)
  const dispatch = useDispatch()

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  const handleSubmit = async () => {
    // Check if we have a valid medical history record
    if (!patMedHist._id) {
      message.error('Medical history record not found. Please refresh the page and try again.')
      return
    }

    setSaving(true)
    try {
      const newRecord = await updateSurgeries(patMedHist._id, surgeryList)
      dispatch(patMedHistReducer.actions.updateState(newRecord))
      setFacet(null)
    } catch (error) {
      message.error(handleError(error))
    } finally {
      setSaving(false)
    }
  }

  const handleAddSurgery = () => {
    if (!inputValue.trim()) {
      message.error('Please provide a surgery description')
      return
    }

    setSurgeryList([
      ...surgeryList,
      {
        description: inputValue.trim().toUpperCase(),
        date: selectedYear
      }
    ])
    setInputValue('')
    setSelectedYear(null)
    setShowModal(false)
  }

  const handleRemoveSurgery = (index) => {
    setSurgeryList(surgeryList.filter((_, i) => i !== index))
  }

  return (
    <div className='desktop-surgeries-container'>
      <DesktopSectionHeader
        icon={faSyringe}
        title='Surgical History'
        subtitle='Track any surgeries or medical procedures you have had'
      />

      <div className='desktop-surgeries-content'>
        {/* Add New Surgery Button */}
        <Card className='desktop-add-surgery-card'>
          <Button
            type='primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setShowModal(true)}
            size='large'
            className='desktop-add-surgery-btn'
          >
            Add Surgery
          </Button>
        </Card>

        {/* Surgeries List */}
        <Row gutter={[24, 24]}>
          {surgeryList.map((surgery, index) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={index}>
              <Card className='desktop-surgery-card'>
                <div className='desktop-surgery-header'>
                  <div className='desktop-surgery-icon'>
                    <FontAwesomeIcon icon={faSyringe} />
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => handleRemoveSurgery(index)}
                    className='desktop-remove-surgery-btn'
                  />
                </div>

                <div className='desktop-surgery-info'>
                  <h4 className='desktop-surgery-description'>{surgery.description}</h4>
                  <Text type='secondary' className='desktop-surgery-date'>
                    {surgery.date ? dayjs(surgery.date).format('YYYY') : 'Unknown date'}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {surgeryList.length === 0 && (
          <Card className='desktop-empty-state'>
            <div className='desktop-empty-content'>
              <FontAwesomeIcon icon={faSyringe} className='desktop-empty-icon' />
              <h3>No Surgeries Added</h3>
              <p>Click "Add Surgery" to start tracking your surgical history.</p>
            </div>
          </Card>
        )}
      </div>

      {/* Add Surgery Modal */}
      <Modal
        title='Add Surgery'
        open={showModal}
        onCancel={() => {
          setShowModal(false)
          setInputValue('')
          setSelectedYear(null)
        }}
        footer={null}
        className='desktop-surgery-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Surgery Description</label>
            <Input.TextArea
              placeholder='Enter surgery or procedure details...'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              rows={3}
              style={{ resize: 'none' }}
            />
          </div>

          <div className='desktop-form-field'>
            <label>Surgery Date</label>
            <Button block onClick={() => setShowYearPicker(true)} className='desktop-date-button'>
              {selectedYear ? dayjs(selectedYear).format('YYYY') : 'Select year'}
            </Button>
          </div>

          <div className='desktop-modal-actions'>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type='primary' onClick={handleAddSurgery}>
              Add Surgery
            </Button>
          </div>
        </div>
      </Modal>

      {/* Year Picker */}
      <DatePicker
        isVisible={showYearPicker}
        onDateSelect={(date) => {
          setSelectedYear(date.toISOString())
          setShowYearPicker(false)
        }}
        onCancel={() => setShowYearPicker(false)}
        mode='year'
        maximumDate={new Date()}
      />

      <DesktopMedicalHistoryNav
        prevFunction={() => setFacet(null)}
        nextFunction={handleSubmit}
        prevText='Back to Overview'
        nextText='Save Surgeries'
      />
    </div>
  )
}

export default DesktopSurgeries
